import Minze, { Attrs, EventListeners, MinzeElement } from 'minze'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { Routing } from '../../../modules/routing'

Locale.getInstance().registerKeySet('fr', 'LvcDraftCard', {
  lastModified: 'Dernière modification',
  audience: 'Auditoire(s)',
  language: 'Langue(s)',
})
Locale.getInstance().registerKeySet('en', 'LvcDraftCard', {
  lastModified: 'Last modified',
  audience: 'Audience(s)',
  language: 'Language(s)',
})

export interface LvcDraftCard {
  draftId: string
  name: string
  languages: string
  audiences: string
  disciplines: string
  lastModified: string
  dataType?: string
}

export class LvcDraftCard extends MinzeElement {
  attrs: Attrs = [
    ['draft-id', null],
    ['name', null],
    ['languages', null],
    ['audiences', null],
    ['disciplines', null],
    ['last-modified', null],
    ['data-type', 'event'],
  ]

  // When a draft card is clicked, navigate to the draft edit flow
  onClickDraft = (e: Event) => {
    if ((e.target as HTMLElement).closest('#delete-draft-button')) return
    const mainModule = MainModule.getInstance()
    const language = mainModule.getLocale().getLanguage()
    const draftId = this.draftId

    if (this.dataType === 'exhibition') {
      mainModule
        .getRouting()
        .goTo(`${Routing.ROUTES[language].partnerCreateExhibition}?draftId=${draftId}`)
      return
    }

    if (this.dataType === 'festival') {
      mainModule
        .getRouting()
        .goTo(`${Routing.ROUTES[language].partnerCreateFestival}?draftId=${draftId}`)
      return
    }
    mainModule
      .getRouting()
      .goTo(`${Routing.ROUTES[language].partnerCreateEvent}?draftId=${draftId}`)
  }

  openDeleteDraftModal = (e: Event) => {
    e.stopPropagation()
    this.deleteModalOpen = true
    this.rerender()
  }

  cancelDeleteDraft = (e: Event) => {
    e.stopPropagation()
    this.deleteModalOpen = false
    this.rerender()
  }

  confirmDeleteDraft = async (e: Event) => {
    e.stopPropagation()
    const mainModule = MainModule.getInstance()
    const draftId = this.draftId
    const collection = `${this.dataType}_drafts` as
      | 'event_drafts'
      | 'exhibition_drafts'
      | 'festival_drafts'
    try {
      await mainModule.getAuth().deleteDraftItem(draftId, collection)
      Minze.cast('lvc:draft-deleted', { draftId, dataType: this.dataType })
    } catch (error) {
      console.error(error)
    }
    this.deleteModalOpen = false
    this.rerender()
  }

  html = () => `
  <div class="draft-card-button-wrapper" data-draft-id="${this.draftId}" data-type="${this.dataType}" id="draft-card-button">
    <lvc-hand-drawn-box variant="button1" hoverBgToColor="#D4F5FF" bgColor="#BFF0FF">
      <div class="draft-card-button-content">
        <div class="draft-card-button-header">
          <div class="header-container">
            <div class="draft-card-button-label">${this.name}</div>
            <div class="last-modified">${Locale.getKey('LvcDraftCard.lastModified')}: ${new Date(this.lastModified).toLocaleString('fr-FR')}</div>
          </div>
          <div class="draft-data-container">
            <div class="draft-card-info">
              <lvc-language-icon data-width="22" data-height="22" data-color="#1E007D"></lvc-language-icon> 
              <span>${Locale.getKey('LvcDraftCard.language')}: ${this.languages || ''}</span>
            </div>
            <div class="draft-card-info">
              <lvc-persons-icon data-width="30" data-height="30"></lvc-persons-icon>
              <span>${Locale.getKey('LvcDraftCard.audience')}: ${this.audiences || ''}</span>
            </div>
            ${
              this.disciplines
                ? `
            <div class="draft-card-info">
              <lvc-tag-icon data-width="30" data-height="30"></lvc-tag-icon>
              <span class="disciplines">${this.disciplines || ''}</span>
            </div>
            `
                : ''
            }
          </div>
        </div>
        <div class="delete-draft-button-wrapper">
          <button class="delete-draft-button" id="delete-draft-button">
            <lvc-trash-icon></lvc-trash-icon>
          </button>
        </div>
      </div>
    </lvc-hand-drawn-box>
  </div>
  ${
    this.deleteModalOpen
      ? `
    <lvc-closeable-modal no-close-button="true">
      <div class="delete-draft-modal-content">
        <h3 class="delete-draft-modal-title">Supprimer le brouillon</h3>
        <p class="delete-draft-modal-text">Êtes-vous sûr de vouloir supprimer le brouillon "${this.name}" ? Cette action est irréversible.</p>
        <div class="delete-draft-modal-buttons">
          <lvc-button padding="14px 24px" width="100%" id="delete-draft-cancel-button">Annuler</lvc-button>
          <lvc-button padding="14px 24px" width="100%" id="delete-draft-confirm-button">Supprimer</lvc-button>
        </div>
      </div>
    </lvc-closeable-modal>
  `
      : ''
  }
  `

  eventListeners: EventListeners = [
    ['#delete-draft-button', 'click', this.openDeleteDraftModal.bind(this)],
    ['#draft-card-button', 'click', this.onClickDraft.bind(this)],
    ['#delete-draft-cancel-button', 'click', this.cancelDeleteDraft.bind(this)],
    ['#delete-draft-confirm-button', 'click', this.confirmDeleteDraft.bind(this)],
  ]

  css = () => `
    :host {}

    .draft-card-button-header {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .delete-draft-modal-buttons {
      display: flex;
      gap: 20px;
      justify-content: center;
    }

    .draft-card-button-wrapper {
      cursor: pointer;
      flex: 1;
    }

    .delete-draft-button-wrapper {
      display: flex;
      align-items: center;
    }

    .delete-draft-button {
      background: none;
      border: none;
      cursor: pointer;
      height: fit-content;
      padding: 12px;
      transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .delete-draft-button:hover {
      transform: scale(1.1);
    }

    .delete-draft-button:active {
      transform: scale(0.95);
      opacity: 0.8;
    } 

    .delete-draft-button lvc-trash-icon {
      transition: transform 0.2s ease;
    }

    .delete-draft-button:hover lvc-trash-icon {
      transform: rotate(15deg);
    }

    .draft-card-button-content {
      position: relative;
      display: flex;
      gap: 20px;
      padding: 10px;
      color: #1E007D;
      justify-content: space-between;
    }

    .header-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .draft-card-button-label {
      font-size: 18px;
      font-weight: 800;
      line-height: 120%;
      text-transform: uppercase;
    }
    .last-modified {
      font-size: 14px;
    }

    .draft-data-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .draft-card-info {
      font-size: 12px;
      font-weight: 500;
      line-height: 120%;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .disciplines {
      text-decoration: underline;
    }

    @media only screen and (min-width: 992px) {
      .draft-data-container {
        flex-direction: row;
        gap: 16px;
      }
      .draft-card-button-content {
        align-items: center;
        padding: 10px 20px;
      }

      .draft-card-button-label {
        font-size: 22px;
      }
      .last-modified {
        font-size: 16px;
      }
      .draft-card-info {
        font-size: 12px;
      }
    }
  `
}
